import {
  CREATED_AT_JAN_1_2023,
  defaultSite,
  generateId,
  MODELLING_START_AT_JAN_1_2024,
  useSite,
} from "@inrange/building-manager-api-client";
import {
  Building,
  ChangeToSiteDefaults,
  RecursivePartial,
  RecursivePartialSiteAllowStringValues,
  Site,
  SiteAllowStringValues,
  SiteOwnership,
} from "@inrange/building-manager-api-client/models-site";
import { SiteCalculationsProvider } from "@inrange/shared-components";
import { isSiteOwnerOccupied } from "@inrange/theme-components";
import { ReactNode, useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Loading from "../Loading";
import { applySiteUpdate } from "./site-utils";
import SiteEditView from "./SiteEditView";
import { getTodayInTimeZone } from "./siteSections/utils";

type BuildingWithKey = Building & {
  key: string;
  surface_area_sqm: number;
  customised?: boolean;
  sourcePolygonType?: string;
  sourceGeometry?: any;
};

type OwnershipInput = {
  ownershipIndex: number;
  selectedID: string;
  selectedName: string;
  selectedType: string;
  selectedPark?: string;
};

const SiteNew = () => {
  const testMode = !import.meta.env.PROD && import.meta.env.VITE_TEST_MODE;
  document.title = "New site";
  const navigate = useNavigate();
  const { fetchSiteValues, createSite } = useSite({ app: "admin" });

  useEffect(() => {
    if (createSite.isError) {
      setSaveDisabled(false);
      const error = createSite.error as any;
      setAlertStyle("danger");
      setAlertMessage(
        `Error creating site - ${error.response?.data?.message || error.message}`
      );
    }

    if (createSite.isSuccess) {
      const response = createSite.data as any;
      setAlertMessage(undefined);
      navigate(`/site/${response.site_id}/edit`);
    }
  }, [
    createSite.data,
    createSite.error,
    createSite.isError,
    createSite.isSuccess,
    navigate,
  ]);

  const params = new URL(document.location.toString()).searchParams;

  const lat = parseFloat(params.get("lat") || "0");
  const lon = parseFloat(params.get("lon") || "0");
  const bkey = params.get("bkey") || undefined;
  const address = params.get("address") || undefined;

  const [pendingUpdateSite, setPendingUpdateSite] = useState<
    ChangeToSiteDefaults[] | undefined
  >(undefined);

  // previewSite contains a complete view of the new site which is being constructed
  const [isDefaultSiteSet, setIsDefaultSiteSet] = useState(false);
  const [previewSite, setPreviewSite] = useState<SiteAllowStringValues>(
    {} as SiteAllowStringValues
  );
  useEffect(() => {
    if (!isDefaultSiteSet && fetchSiteValues.isSuccess) {
      const newSite = defaultSite(
        fetchSiteValues.data.site,
        fetchSiteValues.data.options,
        generateId(),
        lat,
        lon,
        bkey,
        address
      );
      if (testMode) {
        newSite.createdAt = CREATED_AT_JAN_1_2023;
        newSite.modellingFromDate = MODELLING_START_AT_JAN_1_2024;
      }
      setPreviewSite(newSite as SiteAllowStringValues);
      setIsDefaultSiteSet(true);
    }
  }, [
    fetchSiteValues.data,
    fetchSiteValues.isSuccess,
    lat,
    lon,
    bkey,
    address,
    testMode,
    isDefaultSiteSet,
  ]);

  // Save related state
  const [saveWarnings, setSaveWarnings] = useState<string[] | undefined>(
    undefined
  );
  const [saveDisabled, setSaveDisabled] = useState(false);
  const [hasUnsavedBuildings, setHasUnsavedBuildings] = useState(false);
  const [alertStyle, setAlertStyle] = useState<string>("secondary");
  const [alertMessage, setAlertMessage] = useState<
    ReactNode | string | undefined
  >(undefined);

  // Site bools
  const [isOwnerOccupied, setIsOwnerOccupied] = useState(false);

  // Site attribute trackers
  const [newBuildings, setNewBuildings] = useState<BuildingWithKey[]>([]);

  // NOTE: newOwnerships is a weird structure - it's an object with integer index keys,
  // so it's *almost* an array, but not quite
  const [newOwnerships, setNewOwnerships] = useState<
    Record<number, SiteOwnership>
  >({});

  useEffect(() => {
    setIsOwnerOccupied(isSiteOwnerOccupied(Object.values(newOwnerships)));
  }, [newOwnerships]);

  // useCallback ensures this function value is consistent and hence
  // can be used in a useEffect dependency array with no issues
  const setSiteState = useCallback(
    (update: RecursivePartialSiteAllowStringValues) => {
      setPreviewSite((prevSite) => applySiteUpdate(prevSite, update));
    },
    [setPreviewSite]
  );

  if (!fetchSiteValues.isLoading && fetchSiteValues.isError) {
    return <div>Error loading the site.</div>;
  }

  if (Object.keys(previewSite).length === 0 || fetchSiteValues.isLoading) {
    return <Loading label="Loading site data..." />;
  }

  const setBuildingsForPreview = (buildings: BuildingWithKey[]) => {
    const previewBuildings = buildings.map((b) => {
      return {
        id: b.key,
        surfaceAreaSqM: b.surface_area_sqm,
        siteID: previewSite.id,
        geometry: b.geometry,
        updatedAt: Math.floor(Date.now() / 1000),
        polygonType: b.polygonType,
        sourcePolygonType: b.sourcePolygonType,
        sourceGeometry: b.sourceGeometry,
        createdBy: b.createdBy || "",
        createdAt: b.createdAt || 0,
      };
    });
    setSiteState({ buildings: previewBuildings as Building[] });
    setNewBuildings(buildings);
  };

  const onSaveClick = () => {
    setSaveDisabled(true);

    if (
      previewSite.commerciallyOperationalDate &&
      previewSite.commerciallyOperationalDate <
        getTodayInTimeZone(previewSite.timeZone) &&
      !previewSite.operationalStatus?.startsWith("operational")
    ) {
      setSaveWarnings([
        "You've set an operational date in the past on a site that is not operational.",
      ]);
      return;
    }

    saveSite();
  };

  const saveSite = () => {
    setSaveDisabled(true);
    const sitePayload: any = {
      ...previewSite,
      buildings: newBuildings,
    };
    const payload = { site: sitePayload };
    if (testMode) {
      payload.site.createdAt = CREATED_AT_JAN_1_2023;
    }
    setAlertStyle("secondary");
    setAlertMessage("Creating site...");
    createSite.mutate(payload);
  };

  const onOwnerAdded = ({
    ownershipIndex,
    selectedID,
    selectedName,
    selectedType,
    selectedPark,
  }: OwnershipInput) => {
    if (selectedType === "" || !selectedID) return;
    const updatedNewOwnerships = {
      ...newOwnerships,
      [ownershipIndex]: {
        orgID: selectedID,
        name: selectedName,
        ownership: selectedType,
        ...(selectedPark ? { park: selectedPark } : {}),
      },
    };
    setNewOwnerships(updatedNewOwnerships);

    const updatedOwnerships = Object.values(updatedNewOwnerships).sort((a, b) =>
      `${a.orgID}`.localeCompare(`${b.orgID}`)
    );
    setSiteState({ siteOwnerships: updatedOwnerships });
  };

  const setNewSiteName = (
    siteName: string,
    updatedOwnerships: SiteOwnership[]
  ) => {
    if (!siteName && !previewSite.name) return;
    const firstLandlordName = updatedOwnerships.find(
      (ownership) => ownership.ownership === "landlord"
    )?.name;
    const firstOtherName = updatedOwnerships.find(
      (ownership) => ownership.ownership !== "landlord"
    )?.name;
    const siteNamePrefix =
      siteName.split(" \u1806")[0] || previewSite.name.split(" \u1806")[0];
    const firstLandlordSuffix = firstLandlordName
      ? ` \u1806 ${firstLandlordName}`
      : "";
    const firstOtherSuffix = firstOtherName ? ` \u1806 ${firstOtherName}` : "";
    const newSiteName = `${siteNamePrefix}${firstLandlordSuffix}${firstOtherSuffix}`;
    setSiteState({ name: newSiteName });
  };

  return (
    <SiteCalculationsProvider
      site={previewSite as unknown as RecursivePartial<Site>}
      originalSite={{}}
      setSaveDisabled={setSaveDisabled}
      app="admin"
    >
      <SiteEditView
        site={previewSite}
        setSite={setSiteState}
        pendingUpdateSite={pendingUpdateSite}
        setPendingUpdateSite={setPendingUpdateSite}
        hasUnsavedBuildings={hasUnsavedBuildings}
        setHasUnsavedBuildings={setHasUnsavedBuildings}
        onSaveClick={onSaveClick}
        saveSite={saveSite}
        setBuildingsForPreview={setBuildingsForPreview}
        ownerships={Object.values(newOwnerships)}
        onOwnerAdded={onOwnerAdded}
        isOwnerOccupied={isOwnerOccupied}
        saveDisabled={saveDisabled}
        setSaveDisabled={setSaveDisabled}
        saveWarnings={saveWarnings}
        setSaveWarnings={setSaveWarnings}
        mode="new"
        alertStyle={alertStyle}
        setAlertStyle={setAlertStyle}
        alertMessage={alertMessage}
        setAlertMessage={setAlertMessage}
        siteNewURLBuildingID={(previewSite as any).siteNewURLBuildingID}
        setNewSiteName={setNewSiteName}
      />
    </SiteCalculationsProvider>
  );
};

export default SiteNew;
